import { ref, watch } from "vue";
import { toArray } from "#ce/utils/props";
import { isObjectLike } from "#ce/utils/types";

/**
 * The selection model, seeded from the `value` attribute and restored to it on reset.
 *
 * The `value` attribute names the default selection; the `value` property is the live one. A
 * declared prop would install a non-configurable host accessor that returns the seed forever, so
 * the attribute is read through `attrs`. Under `multiple` a JSON array attribute seeds every entry.
 *
 * `model` is always an array. An empty selection reads as the prompt row (`[null]`) while `prompt`
 * is set, wherever the write came from.
 *
 * @param {object} props  Owning component's reactive props; reads `prompt`.
 * @param {{ attrs: Record<string, any>, multiple: import("vue").Ref<boolean>,
 *   returnObject: import("vue").Ref<boolean>, findItem: (value: any) => any,
 *   getItemValue: (item: any) => any }} options
 * @returns {{ model: import("vue").Ref<any[]>, defaultValue: import("vue").Ref<any>,
 *   initialModel: () => any[], promptNormalized: (entries: any[]) => any[],
 *   setSelection: (value: any) => void, resetSelection: () => void }}
 *   `setSelection` is the `value` property's writer. `resetSelection` restores the default.
 */
export function useDefaultSelection(props, { attrs, multiple, returnObject, findItem, getItemValue }) {
  const defaultValue = ref(parseDefault(attrs.value));

  const model = ref(initialModel());

  watch(model, normalizeModel);

  watch(() => attrs.value, syncDefaultSelection);

  watch(multiple, syncDefaultMode);

  function parseDefault(value) {
    if (multiple.value && typeof value === "string" && value.trim().startsWith("[")) {
      try {
        const parsed = JSON.parse(value);

        if (Array.isArray(parsed)) {
          return parsed;
        }
      } catch (error) {
        if (import.meta.env.DEV) {
          console.error(error);
        }
      }
    }

    return value;
  }

  function initialModel(value = defaultValue.value) {
    return promptNormalized(toArray(value ?? []));
  }

  // Normalizing to the same array when there is nothing to do leaves the ref untouched, so this
  // settles in one pass.
  function normalizeModel() {
    model.value = promptNormalized(model.value);
  }

  function promptNormalized(entries) {
    return entries.length === 0 && props.prompt ? [null] : entries;
  }

  function syncDefaultSelection(value) {
    defaultValue.value = parseDefault(value);
    model.value = initialModel();
  }

  function syncDefaultMode() {
    defaultValue.value = parseDefault(attrs.value);
  }

  function setSelection(value) {
    if (value === null || value === undefined || (value === "" && (props.prompt || findItem("") === undefined))) {
      model.value = promptNormalized([]);

      return;
    }

    // A return-object consumer hands the item object back; the model stores its value. Without
    // `return-object` an object is the item's own value, so unwrapping it would lose the selection.
    model.value = toArray(value).map((entry) =>
      returnObject.value && isObjectLike(entry) ? getItemValue(entry) : entry,
    );
  }

  function resetSelection() {
    model.value = initialModel();
  }

  return {
    model,
    defaultValue,
    initialModel,
    promptNormalized,
    setSelection,
    resetSelection,
  };
}
